/** @jsxImportSource @emotion/react */
import { createContext, useContext } from 'react';

import { findCategory, makeUrlAbsolute } from '@common/utlis';
import { LinkSwitched } from '@stories/atoms/Link';
import { PageContext } from '@stories/templates/Context/pageContext';

import type { Theme } from '@emotion/react';
import type { Interpolation } from '@emotion/serialize';
import type { CategoryListItemFromDBDto } from '@vitafy/storefront-api-contracts-fetch';

type CategoryTreeProps = {
  categoryTree: CategoryListItemFromDBDto[];
  cssProps?: Interpolation<Theme>;
  currentCategoryId: number;
};

type CategoryChildrenUnordedListProps = {
  categoryTree: CategoryListItemFromDBDto[];
  cssProps?: Interpolation<Theme>;
};

const CategoryTreeContext = createContext<{ currentCategory?: CategoryListItemFromDBDto }>({
  currentCategory: undefined
});

const CategoryChildrenUnordedList: React.FC<CategoryChildrenUnordedListProps> = ({
  categoryTree,
  cssProps
}) => {
  const { currentCategory } = useContext(CategoryTreeContext);
  const { storeCode } = useContext(PageContext);
  const isCurrentCategoryAncestor = (categoryId) =>
    currentCategory?.path !== undefined && currentCategory.path.includes(categoryId);
  return (
    <ul css={{ padding: '4px 0', ...(cssProps as object) }}>
      {categoryTree
        .filter(({ url }) => url !== 'magazin.html')
        .map(({ id, name, url, type, children }) => (
          <li key={id} css={{ padding: '4px 0' }}>
            <LinkSwitched
              forceAnchorLink={type === 'CMSMediaLink'}
              href={`${type === 'icp' ? '/category' : ''}${makeUrlAbsolute(url)}`}
              pathname={`/[store]/category/[categoryId]`} // always category for better UX
              query={{ store: storeCode, categoryId: id }}
              scroll={false}
              cssProps={(t) => ({
                lineHeight: '120%',
                ...(id === currentCategory?.id && {
                  fontWeight: 'bold',
                  textDecoration: 'underline',
                  textUnderlineOffset: '3px',
                  textDecorationColor: t.color.primary,
                  textDecorationThickness: '3px'
                }),
                ':hover': { color: t.color.hover.copy }
              })}
            >
              {name}
            </LinkSwitched>
            {(isCurrentCategoryAncestor(id) || id === currentCategory?.id) && (children?.length || 0) > 0 && (
              <div css={{ padding: '.5rem 0 .5rem 1rem' }}>
                <CategoryChildrenUnordedList categoryTree={children} />
              </div>
            )}
          </li>
        ))}
    </ul>
  );
};

const CategoryTree: React.FC<CategoryTreeProps> = ({ categoryTree, cssProps, currentCategoryId }) => {
  const currentCategory = findCategory((categoryItem) => categoryItem.id === currentCategoryId, categoryTree);
  return (
    <CategoryTreeContext.Provider value={{ currentCategory }}>
      <CategoryChildrenUnordedList categoryTree={categoryTree} cssProps={cssProps} />
    </CategoryTreeContext.Provider>
  );
};

export default CategoryTree;
export type { CategoryTreeProps };
